import Box from "@/common/components/Box";
import BoxWithHeader from "@/common/components/BoxWithHeader";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Spinner from "@/common/components/Spinner";
import { useGoBack } from "@/common/hooks/useGoBack";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMaintenanceForwarding } from "../../../forwardings/hooks/useMaintenanceForwarding";
import { MaintenanceForwardingData } from "../../../forwardings/types/maintenanceForwarding.type";
import FiscalAssessmentDocumentGroupDocument from "../../components/FiscalAssessmentDocumentGroupDocument";
import MaintenanceFiscalAssessmentHeader from "../../components/MaintenanceDirectiveEntryExecutionHeader";
import { fiscalAssessmentDocumentGroupsKind } from "../../constants/fiscalAssessmentDocumentGroupsKind.constant";
import { useMaintenanceFiscalAssessmentSimpleEntriesAdvance } from "../../hooks/simpleEntries/useMaintenanceFiscalAssessmentSimpleEntriesAdvance";
import { useMaintenanceFiscalAssessmentSimpleEntriesRegress } from "../../hooks/simpleEntries/useMaintenanceFiscalAssessmentSimpleEntriesRegress";
import { MaintenanceForwardingDirectiveEntryExecutionEntryData } from "../../../forwardings/types/forwardings/directiveEntries/executionEntry/executionEntry.type";

const CompanyMaintenanceWorkflowsFiscalAssessmentSimpleEntry = () => {
  const { companyId, workflowId, directiveEntryType, forwardingId, fiscalAssessmentEntryId } = useParams();

  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: forwarding = {} as MaintenanceForwardingData<MaintenanceForwardingDirectiveEntryExecutionEntryData>,
    isPending: forwardingIsPending,
    dataUpdatedAt: forwardingUpdatedAt,
  } = useMaintenanceForwarding(forwardingId);

  const {
    mutate: advance,
    isPending: advanceIsPending,
  } = useMaintenanceFiscalAssessmentSimpleEntriesAdvance({
    fiscalAssessmentEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding', forwardingId]
        });

        goBack({
          fallback: `/companies/${companyId}/maintenance/workflows/${workflowId}/${directiveEntryType}`,
          searchLastRouteFromFallback: true,
        });
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error);
      }
    },
  });

  const {
    mutate: regress,
    isPending: regressIsPending,
  } = useMaintenanceFiscalAssessmentSimpleEntriesRegress({
    fiscalAssessmentEntryId,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding', forwardingId]
        });

        goBack({
          fallback: `/companies/${companyId}/maintenance/workflows/${workflowId}/${directiveEntryType}`,
          searchLastRouteFromFallback: true,
        });
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error);
      }
    },
  });

  const isEveryObjectiveReviewed = useMemo(() => {
    return forwarding?.fiscal_assessment_entry?.document_groups?.every(
      (documentGroup) => documentGroup?.documents?.every(
        (document) => !!document.last_review?.state
      )
    );
  }, [forwardingUpdatedAt]);

  const isAnyObjectiveDisaproved = useMemo(() => {
    return forwarding?.fiscal_assessment_entry?.document_groups?.some(
      (documentGroup) => documentGroup?.documents?.some(
        (document) => document.last_review?.state === 'disapproved'
      )
    );
  }, [forwardingUpdatedAt]);

  return (
    <>
      <div className="inner-screen">
        <MaintenanceFiscalAssessmentHeader />

        {forwardingIsPending && (
          <Box className="flex-1 flex items-center justify-center">
            <Spinner />
          </Box>
        )}

        {!forwardingIsPending && (
          <div className="flex-1 flex overflow-hidden gap-6">
            <Box className="flex-1 overflow-auto p-4 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <p className="text-base font-bold">
                  Realize a vistoria do equipamento
                </p>

                <p className="text-sm">
                  Marque os Objetos que foram executados de maneira adequada, caso algo não esteja da maneira esperada não marque, após confirmação você poderá deixar uma justificativa caso um ou mais Objetos não estejam da maneira esperada, o Estabelecimento será notificado e o problema deverá ser corrigido.
                </p>
              </div>

              <Box className="p-4 flex-1 flex flex-col gap-3">
                {forwarding?.fiscal_assessment_entry?.document_groups?.map((documentGroup) => (
                  <div
                    key={documentGroup.id}
                    className="flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p>
                          {fiscalAssessmentDocumentGroupsKind[documentGroup.kind] || documentGroup.kind}
                        </p>
                      </div>
                    </div>

                    {Number(documentGroup?.documents?.length) > 0 ? (
                      <>
                        {documentGroup.documents.map((document) => (
                          <FiscalAssessmentDocumentGroupDocument
                            key={document.id}
                            document={document}
                          />
                        ))}
                      </>
                    ) : (
                      <div className="h-48 overflow-hidden flex flex-col items-center gap-0 bg-white-100 rounded-md">
                        <img
                          src="/search.gif"
                          className="h-40 -mt-4 pointer-events-none select-none"
                        />

                        <p className="text-sm text-center -mt-2">
                          Não há notas fiscais aqui
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </Box>
            </Box>

            <div>
              <BoxWithHeader
                title="Checklist"
                subtitle="Revisão de notas fiscais"
                bodyClassName="min-h-64 justify-between"
              >
                <div className="flex flex-col gap-2 h-full">
                  {forwarding?.fiscal_assessment_entry?.requirement_list_summary?.steps?.map((step) => (
                    <Checkbox
                      key={step.name}
                      label={step.name}
                      name={step.name.replace(/\s/g, '_').toLowerCase()}
                      checked={step.passed}
                      disabled
                      labelClassName="italic text-xs"
                    />
                  ))}
                </div>

                <div className="flex flex-col gap-2 mt-auto">
                  {isAnyObjectiveDisaproved ? (
                    <Button
                      variant="yellow-black"
                      disabled={!isEveryObjectiveReviewed}
                      onClick={() => regress()}
                      isLoading={regressIsPending}
                      operationKlassToCheckPermission="Admin::Maintenance::FiscalAssessment::SimpleEntries::Regress"
                    >
                      Solicitar revisão
                    </Button>
                  ) : (
                    <Button
                      variant={isEveryObjectiveReviewed ? 'default' : 'outline-disabled'}
                      disabled={!isEveryObjectiveReviewed}
                      onClick={() => advance()}
                      isLoading={advanceIsPending}
                      operationKlassToCheckPermission="Admin::Maintenance::FiscalAssessment::SimpleEntries::Advance"
                    >
                      Avançar
                    </Button>
                  )}
                </div>
              </BoxWithHeader>
            </div>
          </div>
        )}

        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      </div >
    </>
  );
}

export default CompanyMaintenanceWorkflowsFiscalAssessmentSimpleEntry;
